import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OrcamentoAberto } from "app/types/cliente/RespostaOrcamentosAbertos";
import globalMU from "globalMU";
import React, { useMemo, memo, useCallback } from "react";

const estiloOrcamentoEmAberto = require("./orcamento-em-aberto.module.scss").default;
const estiloTabela = require('../../estilos/tabelas.module.scss').default;

type ColunaConfig = {
    texto: string;
    coluna: string;
    estilo: string | null;
};

type CabecalhoTabelaProps = {
    colunas: ColunaConfig[];
    colunaOrdenacao: string;
    ordemOrdenacaoDecrescente: boolean;
    onAlterarOrdenacao: (coluna: string, decrescente: boolean) => void;
};

const CabecalhoTabela = memo(({
    colunas,
    colunaOrdenacao,
    ordemOrdenacaoDecrescente,
    onAlterarOrdenacao
}: CabecalhoTabelaProps) => {
    const handleSortClick = useCallback((coluna: string) => {
        const newOrder = colunaOrdenacao === coluna ? !ordemOrdenacaoDecrescente : false;
        onAlterarOrdenacao(coluna, newOrder);
    }, [colunaOrdenacao, ordemOrdenacaoDecrescente, onAlterarOrdenacao]);

    return (
        <thead>
            <tr>
                {colunas.map((x) => (
                    <th key={x.coluna}
                        id={`OrcamentoEmAberto_Cabecalho_${x.coluna}`}
                    >
                        <div className={`_flex _justify-between ${x.estilo}`}
                            id={`OrcamentoEmAberto_Cabecalho_Div_${x.coluna}`}
                            key={`OrcamentoEmAberto_Cabecalho_Div_${x.coluna}`}
                        >
                            {x.texto}
                            <div>
                                <FontAwesomeIcon
                                    className={estiloTabela.ordenacaoIcone}
                                    id={`OrcamentoEmAberto_Cabecalho_Icone_${x.coluna}`}
                                    key={`OrcamentoEmAberto_Cabecalho_Icone_${x.coluna}`}
                                    icon={colunaOrdenacao === x.coluna 
                                        ? (ordemOrdenacaoDecrescente ? faChevronUp : faChevronDown)
                                        : faChevronDown}
                                    onClick={() => handleSortClick(x.coluna)}
                                />
                            </div>
                        </div>
                    </th>
                ))}
            </tr>
        </thead>
    );
});

export type OrcamentoEmAbertoProps = {
    dados: OrcamentoAberto[];
    cpfCnpj: string;
    nome: string;
    colunaOrdenacao: string;
    ordemOrdenacaoDecrescente: boolean;
    onAlterarOrdenacaoDados: (coluna: string, decrescente: boolean) => void;
    usaFonteDeReceita?: boolean // 487678/449093 
};

const COLUNAS: ColunaConfig[] = [
    { texto: "Filial", coluna: "Filial", estilo: null },
    { texto: "Orçamento", coluna: "NumeroPedido", estilo: null },
    { texto: "Data", coluna: "Data", estilo: "_text-right" },
    { texto: "Valor", coluna: "Valor", estilo: null },
    { texto: "Vendedor", coluna: "Vendedor", estilo: null },
    { texto: "Nome", coluna: "Nome", estilo: null },
];

const COLUNA_FONTE_DE_RECEITA: ColunaConfig = { texto: "Fonte de Receita", coluna: "FonteDeReceita", estilo: null }; // 487678/449093 

export const OrcamentoEmAberto = memo((props: OrcamentoEmAbertoProps) => {
    const colunas = useMemo(() => (
        props.usaFonteDeReceita
            ? [...COLUNAS, COLUNA_FONTE_DE_RECEITA]
            : COLUNAS
    ), [props.usaFonteDeReceita]);

    const linhasTabela = useMemo(() => 
        props.dados?.map((dado) => (
            <tr key={dado.NumeroPedido} className={estiloTabela.linhaClicavel}
                id={`OrcamentoEmAberto_linha_${dado.NumeroPedido}`}
            >
                <td
                    id={`OrcamentoEmAberto_linha_Filial_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_Filial_${dado.NumeroPedido}`}
                >{dado.Filial}</td>
                <td
                    id={`OrcamentoEmAberto_linha_NumPed_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_NumPed_${dado.NumeroPedido}`}
                >{dado.NumeroPedido}</td>
                <td
                    id={`OrcamentoEmAberto_linha_DataPedido_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_DataPedido_${dado.NumeroPedido}`}
                >{globalMU.formatDate(dado.DataPedido)}</td>
                <td className="_text-right"
                    id={`OrcamentoEmAberto_linha_valor_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_valor_${dado.NumeroPedido}`}
                >
                    {globalMU.formatarValorNumerico(dado.ValorPedido, 2, true)}
                </td>
                <td
                    id={`OrcamentoEmAberto_linha_CodVen_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_CodVen_${dado.NumeroPedido}`}
                >{dado.CodigoVendedor}</td>
                <td
                    id={`OrcamentoEmAberto_linha_Vendedor_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_Vendedor_${dado.NumeroPedido}`}
                >{dado.NomeVendedor}</td>
                {props.usaFonteDeReceita &&  // 487678/449093 
                <td
                    id={`OrcamentoEmAberto_linha_FonteReceita_${dado.NumeroPedido}`}
                    key={`OrcamentoEmAberto_linha_FonteReceita_${dado.NumeroPedido}`}
                >{dado.FonteDeReceita}</td>
                }
            </tr>
        ))
    , [props.dados, props.usaFonteDeReceita]);

    return (
        <div className={estiloOrcamentoEmAberto.separador}
            key="OrcamentoEmAberto_Separador"
            id="OrcamentoEmAberto_Separador"
        >
            <div className={estiloOrcamentoEmAberto.form}
                key="OrcamentoEmAberto_Form"
                id="OrcamentoEmAberto_Form"
            >
                <label>CPF/CNPJ</label>
                <div
                    key="OrcamentoEmAberto_CPF_CNPJ"
                    id="OrcamentoEmAberto_CPF_CNPJ"
                >{props.cpfCnpj}</div>

                <label>Nome</label>
                <div
                    key="OrcamentoEmAberto_Nome"
                    id="OrcamentoEmAberto_Nome"
                >{props.nome}</div>
            </div>

            <div className={`_my-8 ${estiloTabela.containerRolavel}`}
                key="OrcamentoEmAberto_ContainerRolavel"
                id="OrcamentoEmAberto_ContainerRolavel"
            >
                <table className={`_table _w-full _border-collapse ${estiloTabela.tabela}`}
                    key="OrcamentoEmAberto_Tabela"
                    id="OrcamentoEmAberto_Tabela"
                >
                    <CabecalhoTabela
                        colunas={colunas}
                        colunaOrdenacao={props.colunaOrdenacao}
                        ordemOrdenacaoDecrescente={props.ordemOrdenacaoDecrescente}
                        onAlterarOrdenacao={props.onAlterarOrdenacaoDados}
                    />
                    <tbody
                        key="OrcamentoEmAberto_Tabela_Corpo"
                        id="OrcamentoEmAberto_Tabela_Corpo"
                    >
                        {linhasTabela}
                    </tbody>
                </table>
            </div>
        </div>
    );
});
